import { forwardRef } from 'react';
import { cn } from '@/lib/utils/cn';
import { DictationButton } from './DictationButton';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  /** Optional helper description shown below the label. Same as `hint`. */
  description?: string;
  error?: string;
  hint?: string;
  showCount?: boolean;
  maxLength?: number;
  /** Show a mic button that appends dictated speech to the value. */
  enableDictation?: boolean;
  /** Language tag for dictation (default 'en-US'). */
  dictationLang?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, description, error, hint, showCount, maxLength, id, className, value, enableDictation, dictationLang, onChange, ...props }, ref) => {
    const textareaId  = id ?? `textarea-${Math.random().toString(36).slice(2, 8)}`;
    const errorId     = error ? `${textareaId}-error` : undefined;
    const helperText  = hint ?? description;
    const hintId      = helperText ? `${textareaId}-hint`  : undefined;
    const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
    const charCount   = typeof value === 'string' ? value.length : 0;

    function appendTranscript(text: string) {
      if (!onChange) return;
      const current = typeof value === 'string' ? value : '';
      const sep = current && !/\s$/.test(current) ? ' ' : '';
      const next = current + sep + text;
      // Synthesize a change event so consumers (RHF, controlled forms) work.
      const fake = { target: { value: next } } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
      onChange(fake);
    }

    return (
      <div className="flex flex-col gap-1.5">
        {(label || (showCount && maxLength)) && (
          <div className="flex items-center justify-between">
            {label && (
              <label htmlFor={textareaId} className="text-sm font-medium text-[var(--color-text-primary)]">
                {label}
                {props.required && <span className="text-[var(--color-error)] ml-1" aria-hidden="true">*</span>}
              </label>
            )}
            {showCount && maxLength && (
              <span className="text-xs text-[var(--color-text-secondary)]" aria-live="polite">
                {charCount}/{maxLength}
              </span>
            )}
          </div>
        )}
        <div className="relative">
          <textarea
            ref={ref}
            id={textareaId}
            maxLength={maxLength}
            value={value}
            onChange={onChange}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            className={cn(
              'w-full px-3 py-2.5 text-sm rounded-[var(--radius-md)]',
              'bg-[var(--color-bg-tertiary)] border text-[var(--color-text-primary)]',
              'placeholder:text-[var(--color-text-secondary)]',
              'resize-y min-h-[100px] transition-colors',
              enableDictation && 'pr-12',
              error
                ? 'border-[var(--color-error)]'
                : 'border-[var(--color-border)] focus:border-[var(--color-accent-primary)]',
              className,
            )}
            {...props}
          />
          {enableDictation && (
            <div className="absolute top-2 right-2">
              <DictationButton
                size="sm"
                value={typeof value === 'string' ? value : ''}
                onChange={(next) => {
                  // Re-use synthetic onChange path so RHF/controlled forms see updates.
                  const fake = { target: { value: next } } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
                  onChange?.(fake);
                  // Fallback when consumer didn't pass onChange but did pass value:
                  if (!onChange) appendTranscript(next);
                }}
                lang={dictationLang}
                disabled={props.disabled}
              />
            </div>
          )}
        </div>
        {helperText && !error && (
          <p id={hintId} className="text-xs text-[var(--color-text-secondary)]">{helperText}</p>
        )}
        {error && (
          <p id={errorId} role="alert" className="text-xs text-[var(--color-error)]">{error}</p>
        )}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';
